import React, { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Loader2, Save, Trash2, History, AlertTriangle, CalendarClock } from "lucide-react";

const EMPTY = {
  numero_processo: "", ano: "", data_entrada: "", origem: "", origem_outros: "",
  classificacao: "", tipo_requerimento: "",
  numero_externo: "", nome_requerente: "", unidade_destino: "", data_envio: "",
  data_conclusao: "", assunto: "", status: "",
  despachado_por: "", tipo_deferimento: "",
};

const hojeISO = () =>
  new Intl.DateTimeFormat("fr-CA", { timeZone: "America/Sao_Paulo" }).format(new Date());
const diasEntre = (iso) =>
  iso ? Math.max(0, Math.floor((Date.now() - new Date(`${iso}T00:00:00`).getTime()) / 86400000)) : null;

const FIELDS = [
  ["numero_processo", "Nº Processo", "text"],
  ["ano", "Ano", "number"],
  ["data_entrada", "Data de criação", "date"],
  ["numero_externo", "Nº externo", "text"],
  ["nome_requerente", "Nome do requerente", "text"],
  ["unidade_destino", "Unidade de destino", "text"],
  ["data_envio", "Data de envio", "date"],
  ["data_conclusao", "Data de conclusão", "date"],
  ["status", "Status", "text"],
  ["despachado_por", "Despachado por", "text"],
  ["tipo_deferimento", "Tipo de deferimento", "text"],
];

const ORIGENS = ["INTERNO", "EXTERNO", "E-MAIL", "OUTROS"];
const CLASSIFICACOES = ["SINDICÂNCIA", "PAD", "LICITAÇÃO"];

export const ProcessoSheet = ({ open, onOpenChange, processo, tipoRegistro, onSaved, alertaDias = 30 }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [hist, setHist] = useState([]);
  const [duplicado, setDuplicado] = useState(null);
  const { user } = useAuth();
  const editing = Boolean(processo?._id);

  const diasParado = (() => {
    if (!processo?.data_entrada || processo?.data_conclusao) return null;
    const d = Math.floor((Date.now() - new Date(processo.data_entrada).getTime()) / 86400000);
    return d >= alertaDias ? d : null;
  })();

  useEffect(() => {
    if (open) {
      setForm({
        ...EMPTY,
        ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, processo?.[k] ?? ""])),
        ...(processo?._id ? {} : { data_entrada: hojeISO(), despachado_por: user?.name || "" }),
      });
      setHist([]);
      setDuplicado(null);
      if (processo?._id) {
        api.get(`/processos/${processo._id}/historico`)
          .then(({ data }) => setHist(data))
          .catch(() => setHist([]));
      }
    }
  }, [open, processo, user]);

  const diasCorridos = form.data_conclusao
    ? (form.data_entrada
        ? Math.max(0, Math.floor((new Date(`${form.data_conclusao}T00:00:00`) - new Date(`${form.data_entrada}T00:00:00`)) / 86400000))
        : null)
    : diasEntre(form.data_entrada);

  const set = (k) => (e) => {
    const campoTexto = !["date", "number"].includes(
      (FIELDS.find(([f]) => f === k) || [])[2],
    );
    const valor = campoTexto ? (e.target.value || "").toUpperCase() : e.target.value;
    setForm((f) => ({ ...f, [k]: valor }));
  };

  const checarDuplicado = async () => {
    const numero = (form.numero_processo || "").trim();
    if (!numero || !form.ano) return setDuplicado(null);
    try {
      const { data } = await api.get("/processos/existe", {
        params: {
          numero,
          ano: Number(form.ano),
          tipo_registro: tipoRegistro,
          excluir_id: processo?._id || undefined,
        },
      });
      setDuplicado(data.existe ? { ano: form.ano, numero } : null);
    } catch {
      setDuplicado(null);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = { tipo_registro: tipoRegistro };
      Object.entries(form).forEach(([k, v]) => {
        if (v === "" || v === null) return;
        payload[k] = k === "ano" ? Number(v) : String(v);
      });
      if (editing) await api.put(`/processos/${processo._id}`, payload);
      else await api.post("/processos", payload);
      toast.success(editing ? "Processo atualizado" : "Processo cadastrado");
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Confirma a exclusão deste registro?")) return;
    setBusy(true);
    try {
      await api.delete(`/processos/${processo._id}`);
      toast.success("Registro excluído");
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="processo-sheet">
        <SheetHeader>
          <SheetTitle className="font-display tracking-tight">
            {editing ? `Editar registro` : "Novo registro"}
          </SheetTitle>
          <SheetDescription className="text-xs">
            Toda alteração é registrada na trilha de auditoria.
          </SheetDescription>
        </SheetHeader>

        {diasParado && (
          <div
            data-testid="processo-atraso-alert"
            className="mt-4 flex items-start gap-2 border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
          >
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              Processo sem conclusão há <strong className="mono">{diasParado}</strong> dias.
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
          {duplicado && (
            <div
              data-testid="processo-duplicado-alert"
              className="sm:col-span-2 flex items-start gap-2 border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
            >
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                O processo nº <strong className="mono">{duplicado.numero}</strong> já está cadastrado no
                ano <strong className="mono">{duplicado.ano}</strong>. Informe outro ano ou edite o
                registro existente.
              </span>
            </div>
          )}

          <div className="sm:col-span-2">
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">
              Dias corridos desde a data de cadastro
            </Label>
            <div
              data-testid="processo-dias-box"
              className={`mt-1 flex items-center gap-3 border px-3 py-2.5 ${
                diasCorridos !== null && diasCorridos >= alertaDias && !form.data_conclusao
                  ? "border-red-300 bg-red-50"
                  : "border-border bg-slate-50"
              }`}
            >
              <CalendarClock
                className={`h-4 w-4 shrink-0 ${
                  diasCorridos !== null && diasCorridos >= alertaDias && !form.data_conclusao
                    ? "text-red-600"
                    : "text-slate-400"
                }`}
              />
              <span className="text-2xl font-semibold font-display mono" data-testid="processo-dias-valor">
                {diasCorridos !== null ? diasCorridos : "—"}
              </span>
              <span className="text-xs text-slate-500">
                {diasCorridos === null
                  ? "informe a data de entrada"
                  : form.data_conclusao
                  ? "dia(s) até a conclusão"
                  : `dia(s) corridos · alerta em ${alertaDias} dias`}
              </span>
              {diasCorridos !== null && diasCorridos >= alertaDias && !form.data_conclusao && (
                <span
                  data-testid="processo-dias-alerta"
                  className="ml-auto flex items-center gap-1.5 text-xs font-medium text-red-800 bg-red-100 border border-red-200 px-2 py-1"
                >
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Prazo de {alertaDias} dias atingido
                </span>
              )}
            </div>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">Origem</Label>
            <select
              value={form.origem ?? ""}
              onChange={(e) => setForm((f) => ({
                ...f,
                origem: e.target.value,
                origem_outros: e.target.value === "OUTROS" ? f.origem_outros : "",
              }))}
              data-testid="processo-field-origem"
              className="mt-1 h-9 w-full border border-input bg-white px-2 text-sm rounded-md"
            >
              <option value="">Selecione…</option>
              {ORIGENS.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">
              Classificação
            </Label>
            <select
              value={form.classificacao ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, classificacao: e.target.value }))}
              data-testid="processo-field-classificacao"
              className="mt-1 h-9 w-full border border-input bg-white px-2 text-sm rounded-md"
            >
              <option value="">Não classificado</option>
              {CLASSIFICACOES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          {form.origem === "OUTROS" && (
            <div className="sm:col-span-2">
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">
                Descreva a origem (Outros)
              </Label>
              <Input
                value={form.origem_outros ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, origem_outros: (e.target.value || "").toUpperCase() }))}
                data-testid="processo-field-origem_outros"
                className="mt-1 h-9 text-sm"
                style={{ textTransform: "uppercase" }}
                placeholder="DO QUE SE TRATA"
              />
            </div>
          )}

          <div>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">
              Setor de origem
            </Label>
            <Input
              value={form.tipo_requerimento ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, tipo_requerimento: (e.target.value || "").toUpperCase() }))}
              data-testid="processo-field-tipo_requerimento"
              className="mt-1 h-9 text-sm"
              style={{ textTransform: "uppercase" }}
            />
          </div>

          {FIELDS.map(([key, label, type]) => (
            <div key={key}>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">{label}</Label>
              <Input
                type={type}
                value={form[key] ?? ""}
                onChange={set(key)}
                onBlur={key === "numero_processo" || key === "ano" ? checarDuplicado : undefined}
                data-testid={`processo-field-${key}`}
                style={type === "text" ? { textTransform: "uppercase" } : undefined}
                className={`mt-1 h-9 text-sm ${
                  duplicado && (key === "numero_processo" || key === "ano")
                    ? "border-red-400 focus-visible:ring-red-400"
                    : ""
                }`}
              />
            </div>
          ))}
          <div className="sm:col-span-2">
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">Assunto</Label>
            <Textarea
              value={form.assunto ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, assunto: (e.target.value || "").toUpperCase() }))}
              data-testid="processo-field-assunto"
              className="mt-1 text-sm"
              style={{ textTransform: "uppercase" }}
              rows={3}
            />
          </div>
        </div>

        <div className="flex items-center gap-2 mt-8 pb-6">
          <Button onClick={save} disabled={busy || Boolean(duplicado)} data-testid="processo-save-btn" className="bg-primary hover:bg-[#003366]">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Salvar
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="processo-cancel-btn">
            Cancelar
          </Button>
          {editing && (
            <Button variant="destructive" onClick={remove} disabled={busy} className="ml-auto" data-testid="processo-delete-btn">
              <Trash2 className="h-4 w-4 mr-2" /> Excluir
            </Button>
          )}
        </div>

        {editing && (
          <div className="pb-10" data-testid="processo-historico">
            <div className="flex items-center gap-2 mb-3">
              <History className="h-4 w-4 text-slate-400" />
              <p className="text-[11px] uppercase tracking-wider text-slate-500">
                Histórico de alterações
              </p>
            </div>
            {hist.length === 0 ? (
              <p className="text-sm text-slate-400">Nenhuma alteração registrada para este processo.</p>
            ) : (
              <ol className="relative border-l border-border ml-2 space-y-5">
                {hist.map((h) => (
                  <li key={h._id} className="pl-5 relative" data-testid={`historico-item-${h._id}`}>
                    <span className="absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full bg-primary" />
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-border bg-slate-50 text-slate-600">
                        {h.action}
                      </span>
                      <span className="text-sm font-medium">{h.name || h.username}</span>
                      <span className="text-xs text-slate-400 mono">
                        {h.created_at ? new Date(h.created_at).toLocaleString("pt-BR") : ""}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 mt-0.5">{h.description}</p>
                    {(h.changes || []).length > 0 && (
                      <ul className="mt-1.5 space-y-0.5 text-xs">
                        {h.changes.map((c, i) => (
                          <li key={i} className="mono">
                            <span className="text-slate-500">{c.campo}:</span>{" "}
                            <span className="text-red-700 line-through">{String(c.de ?? "—")}</span>
                            {" → "}
                            <span className="text-emerald-700">{String(c.para ?? "—")}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
